"use client";

import { type FormEvent, useState } from "react";
import type { PortalSession, PublicClientPortal } from "../../lib/clientPortalApi";
import { PortalDirectoryBackLink } from "../../components/PortalDirectoryBackLink";
import { usePortalTranslations } from "../../i18n/client";
import type { PortalTranslationKey } from "../../i18n/messages";

const errors: Record<string, PortalTranslationKey> = {
  "Portal membership is blocked.": "portal.membership.error.blocked",
  "Client portal registration is disabled.": "portal.membership.error.disabled",
  "An invitation is required to join this portal.": "portal.membership.error.invitationRequired",
  "Invitation is invalid.": "portal.membership.error.invalidInvitation",
  "Invitation is invalid, expired or belongs to another email.": "portal.membership.error.expiredInvitation",
  "Invitation has already been accepted.": "portal.membership.error.acceptedInvitation",
  "CRM customer is already linked to another membership.": "portal.membership.error.alreadyLinked",
};

export function PortalMembershipScreen({ portal, session, invitation, onLogout, loggingOut }: {
  portal: PublicClientPortal; session: PortalSession; invitation: string | null; onLogout: () => Promise<void>; loggingOut: boolean;
}) {
  const t = usePortalTranslations();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PortalTranslationKey | null>(null);
  const blocked = session.membershipStatus === "blocked";
  const registrationDisabled = portal.registrationMode === "disabled";
  const pending = session.membershipStatus === "pending" && (!invitation || registrationDisabled);
  const rejected = session.membershipStatus === "rejected" && (!invitation || registrationDisabled);
  const canJoin = !registrationDisabled && (portal.registrationEnabled || Boolean(invitation));

  async function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      const response = await fetch(`/api/client-portals/${encodeURIComponent(portal.slug)}/join`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ invitation }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { message?: string } | null;
        setError(errors[body?.message ?? ""] ?? "portal.membership.error.failed");
        setBusy(false);
        return;
      }
      const target = new URL(window.location.href);
      target.searchParams.delete("invitation"); target.searchParams.delete("signedOut"); target.searchParams.delete("authError"); target.searchParams.delete("authReturn");
      window.location.assign(target.href);
    } catch { setError("portal.membership.error.failed"); setBusy(false); }
  }

  return <main id="portal-content" className="auth-screen"><section className="auth-panel">
    <div className="auth-heading"><PortalDirectoryBackLink /><p>{portal.name}</p><h1>{blocked ? t("portal.membership.blockedTitle") : pending ? t("portal.membership.pendingTitle") : rejected ? t("portal.membership.rejectedTitle") : registrationDisabled ? t("portal.membership.disabledTitle") : invitation ? t("portal.membership.acceptInvitation") : t("portal.membership.title")}</h1></div>
    <p className="auth-alternative">{t("portal.membership.signedInAs")} <strong>{session.identity?.email}</strong></p>
    {error ? <p className="form-error" role="alert">{t(error)}</p> : null}
    {blocked ? <p>{t("portal.membership.blockedDescription")}</p> : pending ? (
      <p>{t("portal.membership.pendingDescription")}</p>
    ) : rejected ? (
      <p>{t("portal.membership.rejectedDescription")}</p>
    ) : <>
      {!canJoin ? <p>{registrationDisabled ? t("portal.membership.disabledDescription") : t("portal.membership.invitationRequired")}</p> : <form className="auth-form" onSubmit={join}>
        <p>{invitation ? t("portal.membership.invitationDescription") : portal.registrationMode === "approval_required" ? t("portal.membership.approvalDescription") : t("portal.membership.joinDescription")}</p>
        <button className="primary-button" disabled={busy}>{busy ? t("portal.membership.busy") : invitation ? t("portal.membership.acceptInvitation") : portal.registrationMode === "approval_required" ? t("portal.membership.requestAccess") : t("portal.access.title")}</button>
      </form>}
    </>}
    <p className="auth-alternative"><a href={`/auth/start?${new URLSearchParams({ slug: portal.slug, prompt: "select_account", ...(invitation ? { invitation } : {}) })}`}>{t("portal.membership.switchAccount")}</a></p>
    <p className="auth-alternative"><button type="button" disabled={busy || loggingOut} onClick={() => void onLogout()}>{t(loggingOut ? "profile.loggingOut" : "portal.membership.logout")}</button></p>
  </section></main>;
}
